import { CircularProgress, IconButton, Menu, MenuItem, Popover, Tooltip } from '@mui/material';
import { CurrencyExchangeOutlined } from '@mui/icons-material';
import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuth, signOut } from 'firebase/auth';
import { toast } from 'react-toastify';
import { useAppSession } from '../../../contexts/AppSessionContext';
import { triggerExposureCalc } from '../../../hooks/useExposure';
import { useUser } from '../../../hooks/useUser';
import { ClearAccountWHSingleMaterial } from '../../../pages/AdminChanges/AdminUtils';
import { createFutureOrder } from '../../../sp-firebase/futureOrderFB';
import { updateAccountInventory } from '../../../sp-firebase/inventoryFB';
import { saveInventoryReport } from '../../../sp-firebase/inventoryReportFB';
import { updateUserAccount } from '../../../sp-firebase/userFB';
import { SpotLockUser } from '../../../sp-types/userType';
import {
  getColorsInventory,
  mockColorFullInventoryData,
  mockColorFullNewFutureOrderData,
} from '../../../utils/mockData';
import AccountSelector from '../../AccountSelector';
import { useAccountStore } from '../../Stores/accountStore';
import { useDemoModeStore } from '../../Stores/demoModeStore';
import { useFutureOrderStore } from '../../Stores/FutureOrdersStore';
import { useTopBarComponent } from '../../Stores/TopBarComponentStore';
import ButtonDesigned from '../../UI-Elements/Buttons/ButtonDesigned';
import { OptionSelect } from '../../UI-Elements/Select/OptionSelect';
import { AdminButtons } from './AdminButtons';
import { CaseSwitchers } from './CaseSwitchers';
import { OverlayDisplayDiv } from '../../UI-Elements/PopoverOverlays/OverlayDisplayDiv';
import ChangePassword from '../../Auth/ChangePassword';
import { getAllAccounts, AccountData } from '../../../services/userManagementService';
import { useBaseCurrency } from '../../../hooks/useBaseCurrency';
import { useCategoryTerms } from '../../../utils/categoryTerms';
import { NotificationBell } from '../../Notifications/NotificationBell';
import { FxRatesDialog } from '../../../pages/HistoricalPrices/components/FxRatesDialog';

const USE_CASE_COLORS = 'useCaseColors';

export const CURRENT_USE_CASES: Record<string, { account: string; allowReset?: boolean }> = {
  'Use case 1': { account: 'useCase1', allowReset: true },
  'Use case 2': { account: 'useCase2', allowReset: true },
  'Use case 3': { account: 'useCase3', allowReset: true },
  [USE_CASE_COLORS]: { account: USE_CASE_COLORS, allowReset: false },
};

interface Props {
  currentPage: string;
}

type Terms = { demand: string; supply: string; hedge: string };

const SUBTITLES: Record<string, string> = {
  Demand: 'Customer orders and sales commitments',
  Supply: 'Inventory on hand and purchase orders',
  Hedges: 'Financial positions and derivatives',
  Dashboard: 'Overview of your demand, supply, hedges, and net exposure',
  'Strategy & Insights': 'Analyse inventory policies, coverage gaps, and hedging strategy',
};

const HINTS: Record<string, string> = {
  Demand: 'What you promised to deliver to customers',
  Supply: 'What you have in stock or coming in',
  Hedges: 'Your insurance against price swings',
};

const TITLE_MAP: Record<string, keyof Terms> = {
  Demand: 'demand',
  Supply: 'supply',
  Hedges: 'hedge',
};

const getPageTitle = (page: string, terms: Terms): string => {
  const key = TITLE_MAP[page];
  return key ? terms[key] : page;
};

const getPageSubtitle = (page: string): string => SUBTITLES[page] || '';
const getPageHint = (page: string): string => HINTS[page] || '';

const TopBar: React.FC<Props> = ({ currentPage }) => {
  const { data: userData } = useUser();
  const { clearSession, user: sessionUser, setSession } = useAppSession();
  const { account } = useAccountStore();
  const terms = useCategoryTerms();
  const { topBarActionComponent: component } = useTopBarComponent();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [currentUseCase, setCurrentUseCase] = useState('');
  const [showAdminTools, setShowAdminTools] = useState(userData?.isAdmin);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [pgAccounts, setPgAccounts] = useState<AccountData[]>([]);
  const [isLoadingAccounts, setIsLoadingAccounts] = useState(false);
  const [fxAnchorEl, setFxAnchorEl] = useState<HTMLElement | null>(null);
  const [accountMenuAnchor, setAccountMenuAnchor] = useState<HTMLElement | null>(null);
  const isAccountMenuOpen = Boolean(accountMenuAnchor);
  const currentAccountLabel = sessionUser?.accountName || sessionUser?.accountId || 'none';
  const baseCurrency = useBaseCurrency();

  const getCurrencySymbol = (currency?: string | null) => {
    if (!currency) return '$';
    switch (currency.toUpperCase()) {
      case 'EUR':
        return '€';
      case 'GBP':
        return '£';
      case 'JPY':
      case 'CNY':
        return '¥';
      case 'ILS':
        return '₪';
      case 'USD':
      default:
        return '$';
    }
  };

  const { isDemoMode, setIsDemoMode } = useDemoModeStore();
  const { resetStore } = useFutureOrderStore();

  React.useEffect(() => {
    if (account?.id) {
      const useCase = Object.entries(CURRENT_USE_CASES).find((item) => item[1].account === account.id)?.[0] || '';
      setCurrentUseCase(useCase);
    }
  }, [account]);
  const anchorRef = useRef<HTMLElement | null>(null);

  const navigate = useNavigate();
  const [loading, setLoading] = React.useState<boolean>(false);

  const switchAccount = async (toAccount: string) => {
    if (account?.id !== toAccount) {
      setLoading(true);

      await updateUserAccount(userData?.id || '', toAccount);

      window.location.reload();
    }
  };

  const restart = async () => {
    setLoading(true);
    if (userData) {
      await ClearAccountWHSingleMaterial(account, userData);
      await triggerExposureCalc({
        accountId: account.id || '',
        materialIds: account.formulasToAccount.map((item) => item.formulaId),
      });
    }
    if (account.id === USE_CASE_COLORS) {
      await ClearAccountWHSingleMaterial(account, userData || ({} as SpotLockUser));
      await createFutureOrder(account.id, mockColorFullNewFutureOrderData('myIOrder'));
      await updateAccountInventory(account.id, mockColorFullInventoryData(account.id));
      await Promise.all(getColorsInventory(account.id).map((a) => saveInventoryReport(a, true)));
      await triggerExposureCalc({
        accountId: account.id || '',
        materialIds: account.formulasToAccount.map((item) => item.formulaId),
      });
    }
    setLoading(false);
    window.location.reload();
  };

  const logout = async () => {
    console.log('👋 [AUTH] Logout requested for:', userData?.email || 'unknown user');
    try {
      const auth = getAuth();
      await signOut(auth);
    } catch (e) {
      console.error('Logout error:', e);
    }
    // Clear app session state
    clearSession();
    // Clear sessionStorage persistence
    sessionStorage.removeItem('uid');
    sessionStorage.removeItem('user');
    sessionStorage.removeItem('loginIntent');
    sessionStorage.removeItem('authSuccess');
    sessionStorage.removeItem('authError');
    navigate('/login');
  };

  const loadPgAccounts = async () => {
    if (!userData?.isSuperadmin || pgAccounts.length > 0 || isLoadingAccounts) return;
    try {
      setIsLoadingAccounts(true);
      const accounts = await getAllAccounts();
      setPgAccounts(accounts);
    } catch (e) {
      console.error('[TopBar] Failed to load Postgres accounts for switcher', e);
    } finally {
      setIsLoadingAccounts(false);
    }
  };

  const handleOpenMenu = () => {
    // Open the user dropdown (Logout etc.) when clicking the user chip
    setIsMenuOpen(true);
  };

  const handleOpenAccountMenu = (event: React.MouseEvent<HTMLSpanElement>) => {
    if (!userData?.isSuperadmin) return;
    // Prevent the click from bubbling to the user chip, so we don't open the Logout menu too
    event.stopPropagation();
    event.preventDefault();
    setAccountMenuAnchor(event.currentTarget);
    void loadPgAccounts();
  };

  const handleCloseAccountMenu = () => {
    setAccountMenuAnchor(null);
  };

  const handleSwitchAccount = (newAccountId: string) => {
    if (!sessionUser) return;
    if (!newAccountId || newAccountId === sessionUser.accountId) return;
    handleCloseAccountMenu();

    const selected = pgAccounts.find((a) => a.id === newAccountId);

    // Update in-memory session user so hooks like AccountSelector pick up the new active account
    const updatedUser = {
      ...sessionUser,
      accountId: newAccountId,
      accountName: selected?.name || sessionUser.accountName,
    } as SpotLockUser;
    setSession('authenticated', updatedUser);

    // Persist to sessionStorage so reloads keep the chosen account
    try {
      const existingUserStr = sessionStorage.getItem('user');
      if (existingUserStr) {
        const stored = JSON.parse(existingUserStr);
        stored.accountId = newAccountId;
        if (selected?.name) {
          stored.accountName = selected.name;
        }
        sessionStorage.setItem('user', JSON.stringify(stored));
      }
    } catch (e) {
      console.error('[TopBar] Failed to persist switched account to sessionStorage', e);
    }

    // Reload the current page so all charts and state fully reload for the new account
    window.location.reload();
  };

  const toggleDemo = () => {
    setShowAdminTools((show) => !show);
  };

  const pageTitle = getPageTitle(currentPage, terms);
  const subtitle = getPageSubtitle(currentPage);
  const hint = getPageHint(currentPage);

  return (
    <div className="flex px-4 min-h-[3.75rem] items-center justify-between  shadow-md bg-white  overflow-x-hidden ">
      {userData?.isDemo ? (
        <div className="flex items-center gap-3">
          <h4
            className={`font-normal cursor-pointer ${isDemoMode ? 'text-blue-600' : ''} `}
            onClick={() => switchAccount(USE_CASE_COLORS)}
          >
            {pageTitle} {isDemoMode && '- Demo Mode'}
          </h4>
          {subtitle && (
            <>
              <span className="text-gray-400">•</span>
              <p className="text-sm text-gray-500">{subtitle}</p>
            </>
          )}
          {hint && (
            <span className="px-2.5 py-1 bg-blue-50 text-blue-700 text-xs rounded-full border border-blue-200">
              💡 {hint}
            </span>
          )}
        </div>
      ) : (
        <div className="flex items-center gap-3">
          <h4 className="font-semibold text-gray-800">{pageTitle}</h4>
          {subtitle && (
            <>
              <span className="text-gray-400">•</span>
              <p className="text-sm text-gray-500">{subtitle}</p>
            </>
          )}
          {hint && (
            <span className="px-2.5 py-1 bg-blue-50 text-blue-700 text-xs rounded-full border border-blue-200">
              💡 {hint}
            </span>
          )}
        </div>
      )}
      <div className="flex flex-row space-x-2  ">
        {showChangePassword && (
          <OverlayDisplayDiv setDisplayOverlay={() => setShowChangePassword(false)} hideClose>
            <ChangePassword setActiveComponent={(_key: string) => setShowChangePassword(false)} />
          </OverlayDisplayDiv>
        )}
      </div>
      <AccountSelector />
      <div className="flex flex-row space-x-8  ">
        {component}
        <div className="flex items-center space-x-2">
          <div className="text-gray-700 text-sm">{account?.id}</div>
          {/* ── User info section ── */}
          <div className="flex items-center gap-1">
            {/* Account chip (superadmin: clickable with own arrow → account switcher) */}
            {(userData?.accountName || userData?.isSuperadmin) && (
              <div
                className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs text-gray-600 ${
                  userData?.isSuperadmin ? 'cursor-pointer hover:bg-gray-100 transition-colors' : ''
                }`}
                onClick={userData?.isSuperadmin ? (e) => handleOpenAccountMenu(e) : undefined}
              >
                <span className="font-medium">{userData?.accountName || 'select account'}</span>
                {(() => {
                  const lastImport = account?.lastImportAt;
                  const thresholds = account?.dataFreshnessThresholds || { greenDays: 7, orangeDays: 14 };
                  if (!lastImport) return null;
                  const importDate = new Date(lastImport);
                  const ageMs = Date.now() - importDate.getTime();
                  const ageDays = ageMs / (1000 * 60 * 60 * 24);
                  const color = ageDays <= thresholds.greenDays ? '#22c55e' : ageDays <= thresholds.orangeDays ? '#f59e0b' : '#9ca3af';
                  const todayStr = new Date().toLocaleDateString('en-IL', { timeZone: 'Asia/Jerusalem' });
                  const importStr = importDate.toLocaleDateString('en-IL', { timeZone: 'Asia/Jerusalem' });
                  const yesterdayDate = new Date(Date.now() - 86400000);
                  const yesterdayStr = yesterdayDate.toLocaleDateString('en-IL', { timeZone: 'Asia/Jerusalem' });
                  const label = importStr === todayStr ? 'today' : importStr === yesterdayStr ? 'yesterday' : `${Math.floor(ageDays)}d ago`;
                  return (
                    <Tooltip title={`Data imported ${label} (${new Date(lastImport).toLocaleDateString()})`} arrow placement="bottom">
                      <span style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: color, display: 'inline-block', flexShrink: 0 }} />
                    </Tooltip>
                  );
                })()}
                {baseCurrency && (
                  <span className="text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">
                    {getCurrencySymbol(baseCurrency)} {baseCurrency}
                  </span>
                )}
                {userData?.isSuperadmin && (
                  <svg className="w-3 h-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                )}
              </div>
            )}

            {/* Divider */}
            {(userData?.accountName || userData?.isSuperadmin) && (
              <div className="w-px h-6 bg-gray-200" />
            )}

            {/* User chip → opens logout menu */}
            <div
              className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors"
              onClick={handleOpenMenu}
            >
              <span className="text-sm font-medium text-gray-800">
                {userData?.name || userData?.email?.split('@')[0] || 'User'}
              </span>
              <span
                className="text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full"
                style={{
                  backgroundColor: userData?.isOwner ? '#fae8ff' :
                    userData?.isAdmin ? '#f3e8ff' :
                    userData?.role === 'admin' ? '#fef3c7' :
                      userData?.role === 'manager' ? '#dcfce7' : '#f3f4f6',
                  color: userData?.isOwner ? '#86198f' :
                    userData?.isAdmin ? '#7c3aed' :
                    userData?.role === 'admin' ? '#b45309' :
                      userData?.role === 'manager' ? '#15803d' : '#4b5563',
                  border: userData?.isOwner ? '1px solid #d946ef' :
                    userData?.isAdmin ? '1px solid #c084fc' :
                    userData?.role === 'admin' ? '1px solid #fbbf24' :
                      userData?.role === 'manager' ? '1px solid #86efac' : '1px solid #d1d5db'
                }}
              >
                {userData?.isOwner ? '👑 Owner' :
                  userData?.isAdmin ? 'Superadmin' :
                  userData?.role === 'admin' ? 'Admin' :
                    userData?.role === 'manager' ? 'Manager' :
                      userData?.role === 'editor' ? 'Editor' : 'Viewer'}
              </span>
              <span ref={anchorRef} className="flex items-center">
                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </span>
            </div>
          </div>

          {/* User menu (logout) — anchored to chevron */}
          <Menu
            open={isMenuOpen}
            onClose={() => setIsMenuOpen(false)}
            anchorEl={anchorRef.current}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            slotProps={{ paper: { sx: { minWidth: 180, mt: 0.5 } } }}
          >
            <div className="px-3 py-1 text-[10px] uppercase tracking-wider text-gray-400 font-semibold">
              {userData?.name || userData?.email?.split('@')[0] || 'User'}
            </div>
            <MenuItem onClick={logout} sx={{ fontSize: '0.8125rem' }}>
              Logout
            </MenuItem>
          </Menu>

          {/* Account switcher menu — anchored to account chip */}
          {userData?.isSuperadmin && (
            <Menu
              open={isAccountMenuOpen}
              onClose={handleCloseAccountMenu}
              anchorEl={accountMenuAnchor}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              slotProps={{ paper: { sx: { maxHeight: 320, minWidth: 180, mt: 0.5 } } }}
            >
              <div className="px-3 py-1 text-[10px] uppercase tracking-wider text-gray-400 font-semibold">
                Switch account
              </div>
              {isLoadingAccounts ? (
                <MenuItem disabled sx={{ fontSize: '0.8125rem' }}>
                  <CircularProgress size={14} sx={{ mr: 1 }} /> Loading…
                </MenuItem>
              ) : (
                pgAccounts.map((acc) => (
                  <MenuItem
                    key={acc.id}
                    selected={acc.id === sessionUser?.accountId}
                    onClick={() => handleSwitchAccount(acc.id)}
                    sx={{ fontSize: '0.8125rem' }}
                  >
                    {acc.name || acc.id}
                  </MenuItem>
                ))
              )}
            </Menu>
          )}
          <IconButton
            size="small"
            onClick={(e) => setFxAnchorEl(e.currentTarget)}
            sx={{ color: 'rgb(107,114,128)' }}
            title="FX Rates"
          >
            <CurrencyExchangeOutlined sx={{ fontSize: 20 }} />
          </IconButton>
          <Popover
            open={Boolean(fxAnchorEl)}
            anchorEl={fxAnchorEl}
            onClose={() => setFxAnchorEl(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            slotProps={{
              paper: {
                sx: {
                  borderRadius: 2,
                  boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                  mt: 1,
                },
              },
            }}
          >
            <FxRatesDialog onClose={() => setFxAnchorEl(null)} />
          </Popover>
          <NotificationBell />
        </div>
      </div>
    </div>
  );
};

export default TopBar;
